/**
 * 미디어 뷰어 모달 (이미지/영상 전체화면)
 *
 * 제출 내역의 이미지/영상을 클릭하면 전체화면으로 보여주는 모달.
 * 키보드(ESC, 좌우 화살표) 및 인디케이터 도트로 탐색 가능하다.
 */

import { useCallback, useEffect, useState } from 'react'

interface MediaViewerModalProps {
  mediaUrls: string[]
  initialIndex?: number
  onClose: () => void
}

export function MediaViewerModal({
  mediaUrls,
  initialIndex = 0,
  onClose,
}: MediaViewerModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const currentUrl = mediaUrls[currentIndex]
  const isVideo = currentUrl?.match(/\.(mp4|mov|webm)$/i)

  const goNext = useCallback(() => setCurrentIndex((i) => (i + 1) % mediaUrls.length), [mediaUrls.length])
  const goPrev = useCallback(() => setCurrentIndex((i) => (i - 1 + mediaUrls.length) % mediaUrls.length), [mediaUrls.length])

  // 키보드: ESC 닫기, 좌우 화살표 이동
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') goPrev()
      if (e.key === 'ArrowRight') goNext()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose, goNext, goPrev])

  return (
    <div
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      {/* 닫기 버튼 */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-outline-variant transition-colors"
      >
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* 이전/다음 버튼 */}
      {mediaUrls.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); goPrev() }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-outline-variant transition-colors p-2"
          >
            <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goNext() }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-outline-variant transition-colors p-2"
          >
            <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* 미디어 콘텐츠 */}
      <div
        className="max-w-[90vw] max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {isVideo ? (
          <video
            src={currentUrl}
            controls
            autoPlay
            className="max-w-full max-h-[85vh] rounded-lg"
          />
        ) : (
          <img
            src={currentUrl}
            alt=""
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
          />
        )}
      </div>

      {/* 인디케이터 */}
      {mediaUrls.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {mediaUrls.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(i) }}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                i === currentIndex ? 'bg-white' : 'bg-white/40'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
